import { Warning } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Typography, Button, Stack } from '@mui/material'
import { OverlayState } from '@/hooks/useOverlay'
import { useArchiveDocuments } from '@/service-library/hooks/documents'
import queryKeys from '@/service-library/query-keys'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'

type DeleteDocumentsDialogProps = {
  documentIds: string[]
  overlay: OverlayState
  onDeleteError?: () => void
  onDeleteSuccess?: () => void
  onDeleteSettled?: () => void
}

export default function DeleteDocumentsDialog({
  documentIds,
  overlay,
  onDeleteError,
  onDeleteSuccess,
  onDeleteSettled,
}: DeleteDocumentsDialogProps) {
  const { archiveDocuments, isPending, error } = useArchiveDocuments({
    sideEffectQueryKeys: [
      queryKeys.documents.lists(),
      queryKeys.documentRows.lists(),
    ],
    onSuccess: () => {
      overlay.close()
      onDeleteSuccess?.()
    },
    onError: () => {
      onDeleteError?.()
    },
    onSettled: () => {
      onDeleteSettled?.()
    },
  })

  return (
    <Dialog
      maxWidth="sm"
      {...overlay}
      title={`Delete Document${documentIds.length > 1 ? 's' : ''}`}
    >
      <DialogContent>
        {!!error && (
          <Stack direction="row" spacing={2} pb={2}>
            <Warning color="error" />
            <Typography color="error">{(error as Error).message}</Typography>
          </Stack>
        )}

        <Typography>
          Deleting {documentIds.length > 1 ? 'documents' : 'this document'} will
          permanently remove {documentIds.length > 1 ? 'them' : 'it'} from the
          system.
          <br />
          Are you sure you want to proceed?
        </Typography>
      </DialogContent>

      <DialogFooter>
        <Button variant="text" onClick={overlay.close} disabled={isPending}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="error"
          loading={isPending}
          onClick={() => {
            archiveDocuments(documentIds)
          }}
        >
          Delete
        </LoadingButton>
      </DialogFooter>
    </Dialog>
  )
}
